import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { ReportGroupResponse, ReportPageResponse } from '../lib/types'
import { apiRequest } from '@/api/client'

export interface SalesRow {
  channel: 'pos' | 'mobile'
  id: string
  restaurantId: number
  occurredAt: string
  currency: string
  totalGrossCents: number
  paymentMethods: string
  splitPayment: boolean
  orderStatus: string
  paymentStatus: string
  receiptNo: string | null
  operatorId: string | null
  stationId: string | null
}

export interface SalesDetail {
  channel: string
  id: string
  restaurantId: number
  occurredAt: string
  currency: string
  totalGrossCents: number
  orderStatus?: string
  paymentStatus?: string
  paymentMethod?: string
  operatorId?: string
  stationId?: string
  receiptNo?: string
  items: Array<{
    sku: string
    name: string
    qty: number
    unitGrossCents: number
    lineGrossCents: number
    vatCode: string
  }>
  payments?: Array<{
    id: string
    provider: string
    amountCents: number
    currency: string
    status: string
    authorizedAt: string | null
    capturedAt: string | null
  }>
}

export interface SalesFilters {
  operators: Array<string>
  stations: Array<string>
}

function buildParams(filters: Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && value !== '' && value !== 'all') {
      if (Array.isArray(value)) {
        if (value.length > 0) params.set(key, value.join(','))
      } else {
        params.set(key, String(value))
      }
    }
  }
  return params.toString()
}

export function useSalesQuery(
  filters: Record<string, unknown>,
  enabled = true,
) {
  const qs = buildParams(filters)
  return useQuery({
    queryKey: ['sales', qs],
    queryFn: () =>
      apiRequest<ReportPageResponse<SalesRow>>(`/api/v1/admin/sales?${qs}`),
    staleTime: 5_000,
    enabled,
  })
}

const SOFT_CAP = 5000

export function useSalesInfiniteQuery(
  filters: Record<string, unknown>,
  enabled = true,
) {
  const baseParams = buildParams(filters)
  return useInfiniteQuery({
    queryKey: ['sales-infinite', baseParams],
    queryFn: ({ pageParam = 1 }) =>
      apiRequest<ReportPageResponse<SalesRow>>(
        `/api/v1/admin/sales?${baseParams}&page=${pageParam}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (last, _all, lastPage) => {
      const loadedRows = lastPage * (last.pageSize || 100)
      if (loadedRows >= SOFT_CAP) return undefined
      return loadedRows < last.totalRows ? lastPage + 1 : undefined
    },
    staleTime: 5_000,
    enabled,
  })
}

export function useSalesGroupedQuery(
  filters: Record<string, unknown>,
  groupBy: string,
  enabled = true,
) {
  const qs = buildParams({ ...filters, groupBy })
  return useQuery({
    queryKey: ['sales-grouped', qs],
    queryFn: () => apiRequest<ReportGroupResponse>(`/api/v1/admin/sales?${qs}`),
    staleTime: 5_000,
    enabled: enabled && !!groupBy,
  })
}

export function useSalesDetail(id: string, channel: string, enabled = true) {
  return useQuery({
    queryKey: ['sales-detail', id, channel],
    queryFn: () =>
      apiRequest<SalesDetail>(`/api/v1/admin/sales/${id}?channel=${channel}`),
    staleTime: 10_000,
    enabled: enabled && !!id,
  })
}

export function useSalesFilters(restaurantId: number | null) {
  return useQuery({
    queryKey: ['sales-filters', restaurantId],
    queryFn: () =>
      apiRequest<SalesFilters>(
        `/api/v1/admin/sales/filters${restaurantId ? `?restaurantId=${restaurantId}` : ''}`,
      ),
    staleTime: 60_000,
    enabled: restaurantId != null,
  })
}
